import { logError } from '@ingenico/util-common';
import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { AppVersionStepsKeys } from '../components/system-package-version-steps/systemPackageVersionSteps.model';
import { verifyHasSystemPackageVersionBuildErrorsApartDeploymentRules } from '../components/system-package-version-steps/utils/systemPackageVersionSteps.util';
import type { SystemPackageAppVersion } from '../pages/create-system-package-version-page/createSystemPackageVersionPage.model';

export function useValidateNewSystemPackageVersion(
  formMethods: UseFormReturn<SystemPackageAppVersion>,
  callbacks?: Record<AppVersionStepsKeys, { readonly success?: () => void }>,
): (key: AppVersionStepsKeys) => void {
  const validateDefinitionStep = useCallback(() => {
    formMethods
      .trigger([
        'definition.version',
        'definition.appDependency',
        'definition.minimumAppVersion',
        'definition.operationalSystemDependency',
        'definition.minimumOSVersion',
        'definition.systemPackageDependency',
        'definition.minimumSystemPackageVersion',
        'definition.releaseNotes',
      ])
      .then((isValid) => {
        if (isValid) {
          callbacks?.[AppVersionStepsKeys.Define]?.success?.();
        }
        return void 0;
      })
      .catch(logError);
  }, [formMethods, callbacks]);

  const triggerBuildStepValidation = useCallback(
    () => formMethods.trigger(['build.applicationFiles', 'build.existingAppFiles']),
    [formMethods],
  );

  const getBuildStepErrors = useCallback(() => {
    const applicationFilesError = formMethods.getFieldState('build.applicationFiles').error;
    const existingAppFilesError = formMethods.getFieldState('build.existingAppFiles').error;
    return {
      ...(applicationFilesError ? { applicationFiles: applicationFilesError } : {}),
      ...(existingAppFilesError ? { existingAppFiles: existingAppFilesError } : {}),
    };
  }, [formMethods]);

  const validateBuildStep = useCallback(() => {
    triggerBuildStepValidation()
      .then((isValid) => {
        if (isValid) {
          callbacks?.[AppVersionStepsKeys.Build]?.success?.();
        } else if (!verifyHasSystemPackageVersionBuildErrorsApartDeploymentRules(getBuildStepErrors())) {
          callbacks?.[AppVersionStepsKeys.DeploymentRules]?.success?.();
        }
        return void 0;
      })
      .catch(logError);
  }, [triggerBuildStepValidation, callbacks, getBuildStepErrors]);

  const validateConfigureStep = useCallback(() => {
    formMethods
      .trigger(['configure.parameterTemplates'])
      .then((isValid) => {
        if (isValid) {
          callbacks?.[AppVersionStepsKeys.Configure]?.success?.();
        }
        return void 0;
      })
      .catch(logError);
  }, [formMethods, callbacks]);

  const validateDeploymentRulesStep = useCallback(() => {
    triggerBuildStepValidation()
      .then((isValid) => {
        if (isValid) {
          callbacks?.[AppVersionStepsKeys.DeploymentRules]?.success?.();
        }
        return void 0;
      })
      .catch(logError);
  }, [triggerBuildStepValidation, callbacks]);

  return useCallback(
    (key: AppVersionStepsKeys) => {
      switch (key) {
        case AppVersionStepsKeys.Define: {
          validateDefinitionStep();
          break;
        }
        case AppVersionStepsKeys.Build: {
          validateBuildStep();
          break;
        }
        case AppVersionStepsKeys.Configure: {
          validateConfigureStep();
          break;
        }
        case AppVersionStepsKeys.DeploymentRules: {
          validateDeploymentRulesStep();
          break;
        }
        default: {
          callbacks?.[AppVersionStepsKeys.Summary]?.success?.();
          break;
        }
      }
    },
    [callbacks, validateDefinitionStep, validateBuildStep, validateConfigureStep, validateDeploymentRulesStep],
  );
}
